import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SubmitButton } from "@/components/ui/submit-button";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { errorHandler } from "@/lib/utils";
import { visitObjectiveService } from "@/services/api/visitObjectives";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";

const formSchema = z.object({
  title: z
    .string()
    .min(1, "O título é obrigatório")
    .max(255, "O título deve ter no máximo 255 caracteres"),
});

type FormValues = z.infer<typeof formSchema>;

const VisitObjectiveForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
    },
  });

  useEffect(() => {
    const loadVisitObjective = async () => {
      if (id) {
        try {
          const response = await visitObjectiveService.show(Number(id));
          form.reset({
            title: response.data.title,
          });
        } catch (err) {
          errorHandler(err, "Erro ao carregar objetivo de visita");
          navigate("/visit-objectives");
        }
      }
    };

    loadVisitObjective();
  }, [id, form.reset, navigate]);

  const { handleSubmit, loading } = useFormSubmit({
    id,
    createService: visitObjectiveService.create,
    updateService: visitObjectiveService.update,
    onSuccess: () => navigate("/visit-objectives"),
  });

  const onSubmit = async (values: FormValues) => {
    await handleSubmit(values);
  };

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>
          {id ? "Editar Objetivo de Visita" : "Novo Objetivo de Visita"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="dark:border-white"
                      placeholder="Ex: Entrega de mercadoria, Prospecção, etc."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/visit-objectives")}
              >
                Cancelar
              </Button>
              <SubmitButton loading={loading} />
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default VisitObjectiveForm;
